import ResourceLoader from "../lib/loaders/ResourceLoader";
import DetectBrowser from "detect-browser"

/**
 * Utility api functions to control browser behaviour
 * @class
 * @alias api.browser
 */
class Browser {
    /** @hideconstructor */
    constructor(api) {
        this.api = api;
        this.hashChangeEventListener = null;
        this.ignoreNextHashChange = false;
        this.setupHashHandler();
    }

    /**
     * Validate if browser is supported
     *
     * @return {boolean}
     */
    isSupported() {
        return DetectBrowser.name === 'chrome' || DetectBrowser.name === 'node';
    }

    /**
     * Setup handling of hash, article id, in the browser location bar
     * @private
     */
    setupHashHandler() {
        this.hashChangeEventListener = window.addEventListener(
            'hashchange',
            function () {
                this.reload();
            }.bind(this)
        );
    }

    /**
     * Sets the title in the browsers tab/window
     * @param {string} title - The title to be displayed
     * @private
     */
    setTitle(title) {
        document.title = title;
    }

    /**
     * Trigger a reload of the writer based on current hash held internally in the api
     */
    reload() {
        if (this.ignoreNextHashChange) {
            this.ignoreNextHashChange = false;
            return;
        }

        window.location.href = this.api.router.getEndpoint() + "/#" + this.getHash();
        window.location.reload();
    }

    /**
     * Get the hash value in the browser location bar. Even though this is normally
     * the same as the article id it is not guaranteed to be consistent at all times!
     *
     * @return {string}
     */
    getHash() {
        if (!window.location.hash) {
            return '';
        }

        return window.location.hash.slice(1);
    }

    /**
     * Set the hash value in the browser location bar. Returns the new hash.
     *
     * @param {string} hash New hash value to set
     * @return {string}
     */
    setHash(hash) {
        if (typeof hash !== 'string') {
            window.location.hash = '';
        }

        window.location.hash = hash;
        return this.getHash();
    }

    /**
     * Adds a script tag in html document
     *
     * @param {string} scriptSource - Script URL
     * @returns {Promise}
     */
    addExternalScript(scriptSource) {
        const resourceLoader = new ResourceLoader()
        return resourceLoader.load({url: scriptSource}, 'js')
    }

    /**
     * Open an URL in a new window
     *
     * @param instructions. See {@link getUrl}
     */
    openInNewWindow(instructions) {
        const url = this.getUrl(instructions)
        window.open(url.href, "_blank")
    }

    /**
     * Gets an URL from the specific instructions
     *
     * @param {object} instructions
     * @param {string} instructions.url - Could be a valid URL, or 'current' which will use the current URL in the browser
     * @param {string} instructions.hash - The hash to use after the URL
     *
     * @returns {HTMLElement}
     */
    getUrl(instructions) {
        const url = document.createElement('a');

        url.href = instructions.url === 'current' ? window.location.href : instructions.url

        if (instructions.hash) {
            url.hash = instructions.hash
        }

        return url
    }

}
export {Browser}
